import { useToast } from 'vue-toastification';
import { PARTNER_FEATURE_FLAGS } from '@/constants/partner-features';
import { hasPartnerFeature } from '@/utils/partner-feature-access';
import {
  formatDuration,
  measurePhrase,
  USAGE_AND_LIMITS_ROUTE_NAME,
} from '@/constants/usage-limits';

/**
 * The 429 body every ryd backend sends when an enforced usage limit is reached (ticket
 * 2026-10-18-usage-limits-and-traffic): `{ error: 'RATE_LIMITED', message, measure, level,
 * retryAfterSeconds }` plus `Retry-After` and `x-ratelimit-*` headers.
 */
export interface RateLimitedBody {
  error: 'RATE_LIMITED';
  message?: string;
  measure?: string;
  level?: string;
  retryAfterSeconds?: number;
}

export function isRateLimitedBody(body: unknown): body is RateLimitedBody {
  return (
    !!body && typeof body === 'object' && (body as { error?: unknown }).error === 'RATE_LIMITED'
  );
}

/** "Usage limit reached: data volume per day. Try again in 3 h 20 min." */
export function usageLimitMessage(body: RateLimitedBody): string {
  const retry = Number(body.retryAfterSeconds);
  const when = Number.isFinite(retry) && retry > 0 ? ` Try again in ${formatDuration(retry)}.` : '';
  return `Usage limit reached: ${measurePhrase(body.measure ?? '')}.${when}`;
}

// One notice per measure until its retry time has passed: a page firing ten calls at a limit
// shows one message, not ten.
const shownUntil = new Map<string, number>();

/** Show the notice once per measure and retry period. Returns whether it was shown. */
export function notifyUsageLimit(body: RateLimitedBody, now = Date.now()): boolean {
  const key = body.measure ?? 'usage';
  if ((shownUntil.get(key) ?? 0) > now) return false;
  const retryMs = Math.max(30, Number(body.retryAfterSeconds) || 60) * 1000;
  shownUntil.set(key, now + retryMs);
  try {
    // Usage & Limits is an AI at ryd page: only point users there who can open it.
    const canOpenUsagePage = hasPartnerFeature(PARTNER_FEATURE_FLAGS.AI_AT_RYD, {
      openForInternalUsers: true,
    });
    useToast().warning(
      canOpenUsagePage
        ? `${usageLimitMessage(body)} See Usage & Limits for details.`
        : usageLimitMessage(body),
      {
        timeout: 10000,
        onClick: canOpenUsagePage
          ? () => {
              void import('@/router').then((m) =>
                m.default.push({ name: USAGE_AND_LIMITS_ROUTE_NAME }),
              );
            }
          : undefined,
      },
    );
  } catch {
    // No toast plugin (tests, early boot): the request error still reaches the caller.
  }
  return true;
}

/** Test helper. */
export function resetUsageLimitNotices(): void {
  shownUntil.clear();
}
